import * as React from "react";
import { connect } from "react-redux";
import { Table } from "react-bootstrap";
import { FormattedDate } from "react-intl";
import moment from "moment";
import {
  Button,
  DatePicker,
  DatePickerInput,
  Modal,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";

import { selectableCOECategories } from "underwriter_dashboard/shared";
import ActionableEventProvider from "underwriter_dashboard/hurdle_editors/CashCloseSummary/ActionableEventProvider";
import ActionableEventNotification from "underwriter_dashboard/hurdle_editors/CashCloseSummary/ActionableEventModule";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import { formatPercent, formatDecimal } from "@eave/shared/utils/percent";
import { ClosingSummaryUpdateRequest } from "@eave/shared/types/api";
import APIClient from "../../APIClient";
import Money from "../../Money";
import { loadApp } from "../actions";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface HurdleEditorProps extends HurdleProps {
  reloadApp: Function;
}

class ClosingSummaryHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      prepaid_interest: null,
      loan_term_dates: null,
      settlement_agent: null,
      settlement_agent_company: null,
      settlement_order_number: null,
      loan_terms: null,
      arm_details: null,
      signing_date: null,
      closing_date: null,
      disbursement_date: null,
      closing_date_change_reason: "",
      closing_date_change_category: "",
      show_coe_reason_modal: false,
    };
  }

  componentDidMount() {
    const { app } = this.props;
    APIClient.get(`/loan_applications/${app.id}/hurdles/closing_summary`).then(
      (responseObject: any) => {
        this.setState({
          prepaid_interest: responseObject.prepaid_interest,
          loan_term_dates: responseObject.loan_term_dates,
          settlement_agent: responseObject.settlement_agent,
          settlement_agent_company: responseObject.settlement_agent_company,
          settlement_order_number: responseObject.settlement_order_number,
          loan_terms: responseObject.loan_terms,
          arm_details: responseObject.arm_details,
          signing_date: app.computed_properties.signing_date,
          closing_date: app.computed_properties.closing_date,
          disbursement_date: app.computed_properties.disbursement_date,
        });
      },
    );
  }

  static renderARMDetails(arm_details) {
    return (
      <div className="col-md-3">
        <Table condensed>
          <thead />
          <tbody>
            <tr>
              <td>ARM Loan</td>
              <td>{arm_details.arm_loan ? "Yes" : "No"}</td>
              <td>Lock Period</td>
              <td>{arm_details.lock_period}</td>
            </tr>
            <tr>
              <td>Margin</td>
              <td>{formatPercent(arm_details.margin * 100)}</td>
              <td>Interest Only</td>
              <td>{arm_details.interest_only}</td>
            </tr>
            <tr>
              <td>Current Index</td>
              <td>{formatDecimal(arm_details.index)}</td>
              <td>Index Based On</td>
              <td>{arm_details.index_based_on}</td>
            </tr>
            <tr>
              <td>First Rate Cap</td>
              <td>{formatPercent(arm_details.first_rate_cap * 100)}</td>
              <td>First Change</td>
              <td>{arm_details.first_change_months}</td>
              <td>
                <FormattedDate value={arm_details.first_change_date} timeZone="UTC" />
              </td>
            </tr>
            <tr>
              <td>Subsequent Rate Cap</td>
              <td>{formatPercent(arm_details.subsequent_rate_cap * 100)}</td>
              <td>Subsequent Change</td>
              <td>{arm_details.subsequent_change_months}</td>
              <td>
                <FormattedDate value={arm_details.subsequent_change_date} timeZone="UTC" />
              </td>
            </tr>
            <tr>
              <td>Lifetime Rate Cap</td>
              <td>{formatPercent(arm_details.lifetime_rate_cap * 100)}</td>
            </tr>
            <tr>
              <td>Floor (Min) Rate</td>
              <td>{formatPercent(arm_details.floor_rate * 100)}</td>
            </tr>
            <tr>
              <td>Ceiling (Max) Rate</td>
              <td>{formatPercent(arm_details.ceiling_rate * 100)}</td>
              <td>Prepayment Penalty</td>
              <td>{arm_details.prepayment_penalty}</td>
            </tr>
            <tr>
              <td>Initial P&amp;I Payment</td>
              <td>{arm_details.initial_pi_payment}</td>
              <td>Assumable</td>
              <td>{arm_details.assumable}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  setSigningDate = e => {
    const newDate = e.length > 0 ? moment(e[0]).format("YYYY-MM-DD") : null;
    this.setState({ signing_date: newDate });
  };

  setClosingDate = e => {
    const newDate = e.length > 0 ? moment(e[0]).format("YYYY-MM-DD") : null;
    this.setState({ closing_date: newDate });
  };

  setCOEDateChangeCategory = (closing_date_change_category: string) => {
    this.setState({ closing_date_change_category });
  };

  setCOEDateChangeReason = (closing_date_change_reason: string) => {
    this.setState({ closing_date_change_reason });
  };

  setDisbursementDate = e => {
    const newDate = e.length > 0 ? moment(e[0]).format("YYYY-MM-DD") : null;
    this.setState({ disbursement_date: newDate });
  };

  setShowCOEReasonModal = (showModal: boolean) => {
    if (!showModal) {
      this.setState({
        show_coe_reason_modal: showModal,
        closing_date_change_reason: "",
        closing_date_change_category: "",
      });
    } else {
      this.setState({
        show_coe_reason_modal: showModal,
      });
    }
  };

  cancelDateChangeReasonModal = () => {
    this.setShowCOEReasonModal(false);
    this.setCOEDateChangeReason("");
  };

  resetScreen = () => {
    const { app, reloadApp } = this.props;
    reloadApp(app.id);
  };

  hasChanged = () => {
    const { app } = this.props;
    const { closing_date, signing_date, disbursement_date } = this.state;
    return (
      signing_date !== app.computed_properties.signing_date ||
      closing_date !== app.computed_properties.closing_date ||
      disbursement_date !== app.computed_properties.disbursement_date
    );
  };

  save = () => {
    const { app, reloadApp } = this.props;
    const {
      closing_date,
      closing_date_change_reason,
      closing_date_change_category,
      signing_date,
      disbursement_date,
    } = this.state;

    const request: ClosingSummaryUpdateRequest = {};
    if (closing_date !== app.computed_properties.closing_date) {
      if (closing_date_change_reason === "" || closing_date_change_category === "") {
        if (app.target_closing_date) {
          // COE Date has changed from the Target Date
          this.setShowCOEReasonModal(true);
          return;
        }
      } else {
        request.closing_date_change_reason = closing_date_change_reason;
        request.closing_date_change_category = closing_date_change_category;
      }
      request.closing_date = closing_date;
    }

    if (signing_date !== app.computed_properties.signing_date) {
      request.operator_signing_date = signing_date;
    }

    if (disbursement_date !== app.computed_properties.disbursement_date) {
      request.operator_disbursement_date = disbursement_date;
    }

    APIClient.put(`/loan_applications/${app.id}`, request)
      .then(() => {
        reloadApp(app.id);
      })
      .catch(() => {
        alert(
          `there was an error saving the changes, please contact engineering or reload the page and try again`,
        );
        throw new Error(
          `there was an error saving the updated closing date changes to loan application Id: ${app.id}.`,
        );
      });
  };

  render() {
    const { app, hurdle, onHide } = this.props;
    const {
      prepaid_interest,
      loan_term_dates,
      settlement_agent,
      settlement_agent_company,
      settlement_order_number,
      loan_terms,
      arm_details,
      signing_date,
      closing_date,
      closing_date_change_reason,
      closing_date_change_category,
      disbursement_date,
      show_coe_reason_modal,
    } = this.state;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <h1>Closing Summary</h1>
        <ActionableEventProvider
          appId={app.id}
          closingDate={app.computed_properties.closing_date}
          reloadApp={this.resetScreen}
        >
          <ActionableEventNotification />
        </ActionableEventProvider>
        {loan_term_dates && (
          <div className="col-md-6">
            <Table condensed>
              <thead />
              <tbody>
                {signing_date ? (
                  <tr>
                    <td>Closing / Signing Date</td>
                    <td>
                      <DatePicker
                        id="date-picker-signing-date"
                        datePickerType="single"
                        dateFormat="m/d/Y"
                        style={{ marginBottom: "10px" }}
                        value={moment(signing_date).format("MM/DD/YYYY")}
                        onChange={e => this.setSigningDate(e)}
                      >
                        <DatePickerInput
                          id="date-picker-signing-date-input-id"
                          name="signing_date"
                          iconDescription="Select a signing date"
                          placeholder="mm-dd-yyyy"
                          type="text"
                          size="sm"
                        />
                      </DatePicker>
                    </td>
                  </tr>
                ) : (
                  <span>&mdash;</span>
                )}
                {closing_date ? (
                  <tr>
                    <td>Close of Escrow / Recording Date</td>
                    <td>
                      <DatePicker
                        id="date-picker-closing-date"
                        datePickerType="single"
                        dateFormat="m/d/Y"
                        style={{ marginBottom: "10px" }}
                        value={moment(closing_date).format("MM/DD/YYYY")}
                        onChange={e => this.setClosingDate(e)}
                      >
                        <DatePickerInput
                          id="date-picker-closing-date-input-id"
                          name="closing_date"
                          iconDescription="Select a closing date"
                          placeholder="mm-dd-yyyy"
                          type="text"
                          size="sm"
                        />
                      </DatePicker>
                    </td>
                  </tr>
                ) : (
                  <span>&mdash;</span>
                )}
                {disbursement_date ? (
                  <tr>
                    <td>Disbursement Date</td>
                    <td>
                      <DatePicker
                        id="date-picker-disbursement-date"
                        datePickerType="single"
                        dateFormat="m/d/Y"
                        style={{ marginBottom: "10px" }}
                        value={moment(disbursement_date).format("MM/DD/YYYY")}
                        onChange={e => this.setDisbursementDate(e)}
                      >
                        <DatePickerInput
                          id="date-picker-disbursement-date-input-id"
                          name="disbursement_date"
                          iconDescription="Select a disbursement date"
                          placeholder="mm-dd-yyyy"
                          type="text"
                          size="sm"
                        />
                      </DatePicker>
                    </td>
                  </tr>
                ) : (
                  <span>&mdash;</span>
                )}
                <tr>
                  <td>Locked At</td>
                  <td>
                    {loan_term_dates.lock_date && (
                      <FormattedDate
                        value={loan_term_dates.lock_date}
                        year="numeric"
                        month="numeric"
                        day="numeric"
                        hour="numeric"
                        minute="numeric"
                        second="numeric"
                        timeZoneName="short"
                        timeZone="America/Los_Angeles"
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Rate Lock Expiration</td>
                  <td>
                    {loan_term_dates.rate_lock_expiration && (
                      <FormattedDate
                        value={loan_term_dates.rate_lock_expiration}
                        year="numeric"
                        month="numeric"
                        day="numeric"
                        hour="numeric"
                        minute="numeric"
                        second="numeric"
                        timeZoneName="short"
                        timeZone="America/Los_Angeles"
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>First Payment Date</td>
                  <td>
                    <FormattedDate value={loan_term_dates.first_payment_date} timeZone="UTC" />
                  </td>
                </tr>
                <tr>
                  <td>Maturity Date</td>
                  <td>
                    <FormattedDate value={loan_term_dates.maturity_date} timeZone="UTC" />
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}

        <div className="col-md-6">
          <Table condensed>
            <thead />
            <tbody>
              <tr>
                <td>Settlement Agent Company</td>
                <td>{settlement_agent_company && settlement_agent_company.name}</td>
              </tr>
              <tr>
                <td>Contact</td>
                <td>{settlement_agent && settlement_agent.name}</td>
              </tr>
              <tr>
                <td>Phone</td>
                <td>{settlement_agent && settlement_agent.phone}</td>
              </tr>
              <tr>
                <td>Email</td>
                <td>{settlement_agent && settlement_agent.email}</td>
              </tr>
              <tr>
                <td>Order Number</td>
                <td>{settlement_order_number}</td>
              </tr>
              <tr>
                <td>Vendor</td>
                <td>
                  {(settlement_agent && settlement_agent.external_id_type === "ClosingCorpID") ||
                  (settlement_agent_company &&
                    settlement_agent_company.external_id_type === "ClosingCorpID")
                    ? "Closing Corp"
                    : "Borrower Chosen"}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
        {loan_terms && (
          <div className="col-md-6">
            <Table condensed>
              <thead />
              <tbody>
                <tr>
                  <td>Loan Purpose</td>
                  <td>Purchase</td>
                </tr>
                <tr>
                  <td>Loan Type</td>
                  <td>{loan_terms.loan_type}</td>
                </tr>
                <tr>
                  <td>Product</td>
                  <td>{loan_terms.product}</td>
                </tr>
                <tr>
                  <td>Loan Term</td>
                  <td>{loan_terms.term}</td>
                </tr>
                <tr>
                  <td>Interest Rate</td>
                  <td>{loan_terms.interest_rate}</td>
                </tr>
                <tr>
                  <td>Loan Amount</td>
                  <td>
                    <Money value={loan_terms.loan_amount} />
                  </td>
                </tr>
                <tr>
                  <td>Subordinate Lien</td>
                  <td>{loan_terms.subordinate_lien}</td>
                </tr>
                <tr>
                  <td>Purchase Price</td>
                  <td>
                    <Money value={loan_terms.purchase_price} />
                  </td>
                </tr>
                <tr>
                  <td>Appraised Value</td>
                  <td>
                    <Money value={loan_terms.appraised_value} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
        {arm_details && ClosingSummaryHurdleEditor.renderARMDetails(arm_details)}
        <div className="col-lg-12">
          <h2>Prepaid Interest</h2>
          {prepaid_interest && (
            <Table striped condensed>
              <thead>
                <tr>
                  <th>Paid By: Borrower</th>
                  <th>Based On: 365 Days</th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th>Amount Per Day</th>
                  <th>Number of Days</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <FormattedDate value={prepaid_interest.start_date} timeZone="UTC" />
                  </td>
                  <td>
                    <FormattedDate value={prepaid_interest.end_date} timeZone="UTC" />
                  </td>
                  <td>${prepaid_interest.per_diem_amount}</td>
                  <td>
                    <p>{prepaid_interest.number_of_days}</p>
                  </td>
                  <td>
                    <Money value={prepaid_interest.total} />
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
        </div>

        <Modal
          open={show_coe_reason_modal}
          modalHeading="Close of Escrow Date Change Reason"
          primaryButtonText="Save"
          secondaryButtonText="Cancel"
          primaryButtonDisabled={
            closing_date_change_reason === "" || closing_date_change_category === ""
          }
          onBlur={() => this.cancelDateChangeReasonModal()}
          onRequestClose={() => this.cancelDateChangeReasonModal()}
          onRequestSubmit={() => this.save()}
        >
          <Select
            light
            id="coe-date-change-category-select"
            name="coe-date-change-category-select"
            labelText="Category"
            placeholder="Select Category"
            value={closing_date_change_category}
            onChange={e => this.setCOEDateChangeCategory(e.target.value)}
          >
            <SelectItem text="Select a category" value="" />
            {Object.values(selectableCOECategories).map(c => (
              <SelectItem key={c.value} text={c.label} value={c.value} />
            ))}
          </Select>
          <TextInput
            id="coe-date-change-reason-text-input"
            value={closing_date_change_reason}
            labelText="Please enter the reason for changing the Close of Escrow Date"
            onChange={e => this.setCOEDateChangeReason(e.target.value)}
          />
        </Modal>

        <footer className={styles.footer}>
          {this.hasChanged() && (
            <div className={styles.buttons}>
              <Button kind="secondary" tabIndex={0} onClick={() => this.resetScreen()}>
                Cancel
              </Button>
              <Button kind="primary" type="submit" tabIndex={0} onClick={() => this.save()}>
                Save
              </Button>
            </div>
          )}
        </footer>
      </HurdleView>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  reloadApp: appID => {
    loadApp(dispatch, appID);
  },
});
export default connect(null, mapDispatchToProps)(ClosingSummaryHurdleEditor);
